/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * STRAŻNIK SUPERLATYWU
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Skąd to się wzięło: przegląd konceptu R5-15-009 wykrył insight, który ogłaszał „największy
 * spadek punktualności w całej sieci" dla szkoły, która NIE miała największego spadku — a inny
 * ekran pokazywał dla tej samej placówki inne liczby. Produkt podający na dwóch ekranach dwie
 * prawdy o tym samym bycie jest gorszy niż brak produktu, bo błąd wygląda w nim na wynik.
 *
 * W makiecie naprawiliśmy to, wyliczając liczby ze zbioru. W realnym produkcie liczby przychodzą
 * z backendu, więc frontend nie może ich „poprawić" — ale MOŻE i POWINIEN wykryć sprzeczność
 * zanim pokaże ją właścicielowi jako fakt.
 *
 * ZASADA: warstwa prezentacji nie jest właścicielem liczb, ale jest ostatnim miejscem, w którym
 * da się złapać zdanie, któremu przeczy załączony do niego dowód.
 *
 * Strażnik NIE ukrywa insightu. Ukrywanie zamieniłoby sprzeczność w ciszę, a cisza jest
 * nieodróżnialna od braku sygnału. Zamiast tego oznacza go jako niespójny — i to jest informacja
 * dla zespołu, a nie dla właściciela.
 */

/** Słowa, po których zdanie przestaje być opisem, a staje się twierdzeniem o skrajności. */
const SUPERLATYWY = [
  'największ',
  'najwyższ',
  'najniższ',
  'najmniejsz',
  'najsilniejsz',
  'najsłabsz',
  'najgorsz',
  'najlepsz',
] as const;

export function zawieraSuperlatyw(tekst: string): boolean {
  const t = tekst.toLocaleLowerCase('pl-PL');
  return SUPERLATYWY.some((s) => t.includes(s));
}

export interface KandydatSuperlatywu {
  /** Identyfikator bytu, o którym mowa (szkoła, danie, restauracja). */
  id: string;
  /** Wartość metryki, na której opiera się twierdzenie o skrajności. */
  wartosc: number | null;
}

export type WynikStraznika =
  | { spojny: true }
  | { spojny: false; powod: string };

/**
 * Sprawdza, czy byt wskazany w superlatywie jest faktycznie skrajny w zbiorze, na którym
 * insight został policzony.
 *
 * @param kierunek 'min' dla „największy spadek" (najniższa wartość), 'max' dla „najwyższy wzrost".
 */
export function sprawdzSuperlatyw(
  idTwierdzony: string,
  kandydaci: readonly KandydatSuperlatywu[],
  kierunek: 'min' | 'max',
): WynikStraznika {
  const zWartoscia = kandydaci.filter((k) => k.wartosc !== null) as Array<{ id: string; wartosc: number }>;

  // Zbiór jednoelementowy albo pusty: nie ma czego porównywać, więc superlatyw jest pusty.
  // To NIE jest sprzeczność, ale też nie jest twierdzeniem o skrajności — i tak to nazywamy.
  if (zWartoscia.length < 2) {
    return {
      spojny: false,
      powod: `Twierdzenie o skrajności przy ${zWartoscia.length} porównywalnych bytach. Superlatyw wymaga co najmniej dwóch.`,
    };
  }

  const skrajna = zWartoscia.reduce((a, b) =>
    kierunek === 'min' ? (b.wartosc < a.wartosc ? b : a) : b.wartosc > a.wartosc ? b : a,
  );

  if (skrajna.id === idTwierdzony) return { spojny: true };

  const twierdzony = zWartoscia.find((k) => k.id === idTwierdzony);
  return {
    spojny: false,
    powod:
      `Insight wskazuje „${idTwierdzony}"` +
      (twierdzony ? ` (${twierdzony.wartosc})` : ' (bytu nie ma w zbiorze)') +
      `, ale skrajną wartością w zbiorze jest „${skrajna.id}" (${skrajna.wartosc}).`,
  };
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
   WALIDACJA WOBEC DEKLARACJI RANKINGU
   ══════════════════════════════════════════════════════════════════════════════════════════════
   Pierwsza wersja strażnika sprawdzała tylko warunek konieczny (próba ≥ 2), bo backend nie mówił,
   PO CZYM sortuje. Po dodaniu deklaracji `ranking` da się sprawdzić rzecz właściwą: czy byt
   wskazany w zdaniu jest faktycznie skrajny WEDŁUG ZADEKLAROWANEJ METRYKI, w ZADEKLAROWANYM
   zbiorze.

   Świadomie NIE wyprowadzamy metryki z treści zdania. Zgadywanie z prozy jest dokładnie tym
   błędem, przed którym ten kontrakt chroni — zdanie da się przeredagować, a deklaracja jest polem.
   ══════════════════════════════════════════════════════════════════════════════════════════════ */

export interface DeklaracjaRankingu {
  metric: string;
  direction: 'MAX' | 'MIN';
  source: string;
}

export interface InsightDoWalidacji {
  insight_id: string;
  title: string;
  fact: string;
  entity_id: number | null;
  ranking?: DeklaracjaRankingu | null;
}

export type OcenaInsightu =
  /** Nie twierdzi o skrajności — nie ma czego sprawdzać. */
  | { ocena: 'NIE_DOTYCZY' }
  /** Twierdzi o skrajności i deklaracja się potwierdza. */
  | { ocena: 'POTWIERDZONY'; metryka: string }
  /** Twierdzi o skrajności, ale backend nie zadeklarował, po czym rankinguje. */
  | { ocena: 'BRAK_DEKLARACJI'; powod: string }
  /** Deklaracja jest, ale zbiór jej przeczy — albo próba jej nie unosi. */
  | { ocena: 'SPRZECZNY'; powod: string };

/**
 * @param kandydaci wartości ZADEKLAROWANEJ metryki dla wszystkich bytów zbioru źródłowego.
 *   Pusta tablica znaczy: nie mamy zbioru do porównania (nie: „zbiór jest pusty").
 */
export function ocenInsight(
  insight: InsightDoWalidacji,
  kandydaci: readonly KandydatSuperlatywu[],
): OcenaInsightu {
  if (!zawieraSuperlatyw(`${insight.title} ${insight.fact}`)) return { ocena: 'NIE_DOTYCZY' };

  if (!insight.ranking) {
    return {
      ocena: 'BRAK_DEKLARACJI',
      powod:
        'Insight twierdzi o skrajności, ale nie deklaruje, według której metryki rankinguje. ' +
        'Takiego zdania nie da się sprawdzić — a niesprawdzalny superlatyw jest w tym produkcie ' +
        'defektem, nie brakiem wygody.',
    };
  }

  // Brak zbioru to NIE jest sprzeczność — po prostu nie mamy czym zweryfikować.
  // Mówimy to wprost, zamiast zaliczać insight jako potwierdzony.
  if (kandydaci.length === 0) {
    return {
      ocena: 'BRAK_DEKLARACJI',
      powod: `Zadeklarowano ranking po „${insight.ranking.metric}" w zbiorze „${insight.ranking.source}", ale ten zbiór nie jest dostępny po stronie klienta — nie ma czym sprawdzić.`,
    };
  }

  const wynik = sprawdzSuperlatyw(
    String(insight.entity_id ?? ''),
    kandydaci,
    insight.ranking.direction === 'MAX' ? 'max' : 'min',
  );

  return wynik.spojny
    ? { ocena: 'POTWIERDZONY', metryka: insight.ranking.metric }
    : { ocena: 'SPRZECZNY', powod: `${wynik.powod} (metryka: ${insight.ranking.metric}, zbiór: ${insight.ranking.source})` };
}
